import {
  Account,
  AccountRole,
  Activity,
  Agent,
  AgentBase,
  Animal,
  AnimalAgent,
  Character,
  Conversation,
  getSchema,
  ImageReference,
  InteractivePersona,
  Manual,
  Message,
  MessageContent,
  Mind,
  MindAgent,
  Persona,
  Relationship,
  User,
  UserAgent,
  WithRef,
} from ".";
import { DocumentData, DocumentReference, FieldValue, Timestamp, arrayUnion } from "@um/contracts/firestore";
import { getFirestoreContractProvider } from "./FirestoreContractProvider";

export function getLatestInsightVersion(mind: Mind): string | undefined {
  if (!mind.insightVersions || Object.keys(mind.insightVersions).length === 0) {
    return undefined;
  }

  return Object.entries(mind.insightVersions).reduce(
    (latest, [key, timestamp]) => {
      if (!latest || (mind.insightVersions?.[latest] && timestamp > mind.insightVersions?.[latest])) {
        return key;
      }
      return latest;
    },
    undefined as string | undefined
  );
}

export function getManualContent(manual: Manual) {
  return manual?.versions?.find((v) => v.id === manual.currentVersion) || (manual?.versions?.length ?? 0) > 0
    ? manual.versions?.[manual.versions.length - 1]
    : undefined;
}

export function getPersona(character: Character | User, personaIdOrAgent?: string | Agent): Persona;
export function getPersona(
  character: Character | User | undefined,
  personaIdOrAgent?: string | Agent
): Persona | undefined;
export function getPersona(
  character: Character | User | undefined,
  personaIdOrAgent?: string | Agent
): Persona | undefined {
  if (!character) {
    return undefined;
  }
  if ("display_name" in character) {
    return { id: "default", name: character.display_name, image: character.image };
  }

  const personaId =
    typeof personaIdOrAgent === "string"
      ? personaIdOrAgent
      : personaIdOrAgent && "personaId" in personaIdOrAgent
        ? personaIdOrAgent.personaId
        : undefined;

  const personas = character?.personas ?? [];

  return (
    (personaId && personas.find((p) => p.id === personaId)) ||
    (character?.defaultPersonaId && personas.find((p) => p.id === character.defaultPersonaId)) ||
    personas.find((p) => p.id === "default") ||
    personas[0]
  );
}

export function getIntroSupportId(character: Character | undefined, personaId?: string): string | undefined {
  if (!character) {
    return undefined;
  }
  if (personaId) {
    return (
      character.personas?.find((p) => p.id === personaId)?.introSupportId ??
      (character as Character & { introSupportId?: string }).introSupportId
    );
  }
  const defaultPersona = getPersona(character);
  if (defaultPersona?.introSupportId) {
    return defaultPersona.introSupportId;
  }
  const fromAnyPersona = character.personas?.find((p) => p.introSupportId)?.introSupportId;
  if (fromAnyPersona) {
    return fromAnyPersona;
  }
  return (character as Character & { introSupportId?: string }).introSupportId;
}

export function hasIntroSupportId(character: Character | undefined): boolean {
  return !!getIntroSupportId(character);
}

export function updatePersonaIntroSupportId(
  personas: Persona[],
  personaId: string,
  introSupportId: string | undefined
): Persona[] {
  return personas.map((persona) => {
    if (persona.id !== personaId) {
      return persona;
    }
    if (introSupportId) {
      return { ...persona, introSupportId };
    }
    const { introSupportId: _removed, ...rest } = persona;
    return rest;
  });
}

export function getInteractivePersona(persona: Persona): InteractivePersona {
  return {
    name: persona.name,
    image: persona.image,
    imageVariations: persona.imageVariations,
  };
}

export function getAccountReference(ref: DocumentReference | WithRef | string): DocumentReference<Account> {
  const fsp = getFirestoreContractProvider();
  if (typeof ref === "string") {
    if (!ref.includes("/")) {
      return fsp.getDocument(`Account/${ref}`);
    }
    ref = fsp.getDocument(ref);
  } else if ("ref" in ref) {
    ref = ref.ref;
  }
  const path = ref.path.split("/");
  if (path[0] == "Account") {
    return path.length == 2
      ? (ref as DocumentReference<Account>)
      : fsp.getDocument<Account>(path.slice(0, 2).join("/"));
  }
  throw new Error(`The reference ${ref.path} is under an account`);
}

/**
 * Get a user's role from an account
 * @param account - The account to search
 * @param userId - The user ID to find
 * @returns The user's AccountRole, or undefined if not found
 */
export function getUserRole(
  account: Account | WithRef<Account> | null | undefined,
  userId: string | undefined
): AccountRole | undefined {
  if (!account || !userId) return undefined;
  const accountData = "ref" in account ? account : account;
  const userRole = accountData.userRoles?.find((r) => r.agent?.user?.id === userId);
  return userRole?.role;
}

export function isInPublicAccount(ref: DocumentReference): boolean {
  return ref.parent?.parent?.id === "public";
}

// get a name for the user that would be safe to display in a public context such as an adoption history
export function getPublicUserName(
  user: User | Agent | Mind | Animal | string | null | undefined,
  fallback: string = "User"
): string {
  if (!user) return fallback;
  const name =
    typeof user === "string"
      ? user
      : "personas" in user && getPersona(user)?.name
        ? getPersona(user)!.name
        : "name" in user
          ? user.name
          : "display_name" in user
            ? user.display_name
            : undefined;
  // return the first name
  return name ? (name.split(" ")[0] ?? fallback) : fallback;
}

export function getPublicAgent(agent: Agent): Agent {
  // TODO: Make sure that the image is not a photo
  return {
    ...agent,
    name: "user" in agent || "mind" in agent ? getPublicUserName(agent) : agent.name,
  };
}

// use for treating arrays like sets
export function AddRefIfNeeded(
  array: DocumentReference[] | undefined | null,
  ref: DocumentReference
): FieldValue | undefined {
  if (!array) {
    return arrayUnion(ref);
  }
  if (!array.find((r) => r.path === ref.path)) {
    return arrayUnion(ref);
  }
  return undefined;
}

export function getAgentId(agent: Agent) {
  return getReference(agent)?.id;
}

export function getReference<T = DocumentData>(source: WithRef<T> | DocumentReference<T>): DocumentReference<T>;
export function getReference(source: AnimalAgent | AgentBase): DocumentReference<Animal> | undefined;
export function getReference(
  source: UserAgent | AnimalAgent | MindAgent | WithRef | DocumentReference
): DocumentReference;
export function getReference(source: Agent): DocumentReference | undefined;
export function getReference(source: Agent | WithRef | DocumentReference): DocumentReference | undefined {
  if (!source) return;
  // DocumentReference
  if (source instanceof DocumentReference) return source;

  // WithRef
  if ("ref" in source) return source.ref;

  // Agent
  if ("animal" in source) return source.animal;
  if ("mind" in source) return source.mind;
  if ("user" in source) return source.user;

  return;
}

export function getAgent(animal: WithRef<Animal>, persona?: string | Persona): AnimalAgent;
export function getAgent(mind: WithRef<Mind>, persona?: string | Persona): MindAgent;
export function getAgent(user: WithRef<User>): UserAgent;
export function getAgent(character: WithRef<Character>, persona?: string | Persona): AnimalAgent | MindAgent;
export function getAgent(
  character: WithRef<Animal> | WithRef<Mind> | WithRef<User>,
  persona?: string | Persona
): AnimalAgent | MindAgent | UserAgent;
export function getAgent(
  character: WithRef<Animal> | WithRef<Mind> | WithRef<User>,
  persona?: string | Persona
): Agent {
  let agentPersona = typeof persona === "object" ? persona : getPersona(character, persona);
  return {
    name: agentPersona.name!,
    image: agentPersona.image,
    ...(character.ref.parent.id === "Animal" ? { animal: character.ref } : {}),
    ...(character.ref.parent.id === "Mind" ? { mind: character.ref } : {}),
    ...(character.ref.parent.id === "User" ? { user: character.ref } : {}),
    // dont include the personaId if it is the default persona, this will use the symantics of the default persona without locking into a specific persona (though we may want to revisit this)
    ...(agentPersona.id && agentPersona.id !== getPersona(character)?.id ? { personaId: agentPersona.id } : undefined),
  };
}

export function getId(
  target: WithRef | DocumentReference | AnimalAgent | UserAgent | MindAgent | string | { id: string }
) {
  if (typeof target === "string") {
    return target.split("/").pop() || target;
  }
  if ("id" in target) {
    return target.id;
  }
  return getReference(target).id;
}

export function activitiesToConversations(activities: WithRef<Activity>[]): Conversation {
  const conversations = activitiesToMessages(activities);
  return {
    messages: conversations.flatMap((c) => c.messages),
    images: conversations.flatMap((c) => c.images).filter(Boolean) as ImageReference[],
  };
}

export function activitiesToMessages(activities: WithRef<Activity>[]): Conversation[] {
  return activities.map((activity) => {
    const response = activity.response;
    const images: ImageReference[] = [
      ...(activity.image ? [activity.image] : []),
      ...(response?.image ? [response.image] : []),
    ];
    return {
      images,
      messages: [
        {
          id: activity.ref.id,
          isGenerated: true,
          from: activity.from,
          created: activity.releaseAt || activity.created,
          contents: [
            {
              dynamicContent: activity.dynamicContent,
              response: activity.response,
            },
          ],
        },
        ...(activity.response
          ? [
              {
                id: activity.ref.id + "-response",
                from: activity.completedBy,
                created: activity.updated!,
                contents: getResponseContent(activity.response),
              } as Message,
            ]
          : []),
        ...(activity.response?.confirmationMessage
          ? [
              {
                id: activity.ref.id + "-confirm",
                from: activity.from,
                isGenerated: true,
                created: activity.updated!,
                contents: [
                  {
                    text: activity.response.confirmationMessage,
                  },
                ],
              } as Message,
            ]
          : []),
      ],
    } as Conversation;
  });
}

export function getResponseContent(response: any): MessageContent[] {
  const content: MessageContent[] = [];
  if (response.actionLabel) {
    content.push({
      text: response.actionLabel,
    });
  }
  if (response.text) {
    content.push({
      text: response.text,
    });
  }
  if (response.image) {
    content.push({
      imageId: response.image.id,
    });
  }
  if (content.length == 0) {
    content.push({
      text: "```\n" + JSON.stringify(response, null, 2) + "\n```",
    });
  }
  return content;
}

export const firestoreReplacer = (_: string, value: any) => {
  if (value instanceof DocumentReference) {
    return value.path;
  }
  if (value && typeof value === "object" && "ref" in value && value.ref && value.ref instanceof DocumentReference) {
    return value.ref.path;
  }
  if (value instanceof Timestamp) {
    return value.toDate().toString();
  }
  // for an unknown reason Timestamp is stripped of its type information during JSON.stringify so we have to reconstruct it
  if (value && typeof value === "object" && "seconds" in value && "nanoseconds" in value) {
    return Timestamp.fromMillis(value.seconds * 1000 + value.nanoseconds / 1000000)
      .toDate()
      .toString();
  }
  return value;
};
